/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/naming-convention */
import {AfterViewInit, Component, ElementRef, ViewChild} from '@angular/core';
import {environment} from '../../../environments/environment';

declare let Redoc: any;

/**
 * Full Redoc CE theme shape (static values only — matches Redoc merge expectations).
 * Light, high-contrast docs that align with Gatewee blues; code panel is light slate, not dark-on-dark.
 */
const redocTheme = {
  spacing: {
    unit: 5,
  },
  colors: {
    tonalOffset: 0.2,
    primary: {
      main: '#2563eb',
      light: '#3b82f6',
      dark: '#1d4ed8',
      contrastText: '#ffffff',
    },
    success: {
      main: '#15803d',
      light: '#22c55e',
      dark: '#14532d',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#d97706',
      light: '#f59e0b',
      dark: '#b45309',
      contrastText: '#ffffff',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
      contrastText: '#ffffff',
    },
    gray: {
      50: '#fafafa',
      100: '#f4f4f5',
    },
    text: {
      primary: '#262626',
      secondary: '#52525b',
    },
    border: {
      dark: 'rgba(0, 0, 0, 0.1)',
      light: '#ffffff',
    },
    responses: {
      success: {
        color: '#15803d',
        backgroundColor: 'rgba(21, 128, 61, 0.08)',
        tabTextColor: '#15803d',
      },
      error: {
        color: '#dc2626',
        backgroundColor: 'rgba(220, 38, 38, 0.08)',
        tabTextColor: '#dc2626',
      },
      redirect: {
        color: '#d97706',
        backgroundColor: 'rgba(217, 119, 6, 0.12)',
        tabTextColor: '#d97706',
      },
      info: {
        color: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
        tabTextColor: '#2563eb',
      },
    },
    http: {
      get: '#15803d',
      post: '#2563eb',
      put: '#9333ea',
      options: '#ca8a04',
      patch: '#ea580c',
      delete: '#dc2626',
      basic: '#71717a',
      link: '#0891b2',
      head: '#a855f7',
    },
  },
  schema: {
    linesColor: '#d4d4d8',
    defaultDetailsWidth: '75%',
    typeNameColor: '#71717a',
    typeTitleColor: '#262626',
    requireLabelColor: '#dc2626',
    labelsTextSize: '0.9em',
    nestingSpacing: '1em',
    nestedBackground: '#fafafa',
    arrow: {
      size: '1.1em',
      color: '#71717a',
    },
  },
  typography: {
    fontSize: '14px',
    lineHeight: '1.5em',
    fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif',
    fontWeightRegular: '400',
    fontWeightBold: '600',
    fontWeightLight: '300',
    smoothing: 'antialiased',
    optimizeSpeed: true,
    headings: {
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '600',
      lineHeight: '1.35em',
    },
    code: {
      fontSize: '13px',
      fontFamily:
        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      lineHeight: '1.5em',
      fontWeight: '400',
      color: '#c2410c',
      backgroundColor: 'rgba(234, 88, 12, 0.08)',
      wrap: false,
    },
    links: {
      color: '#2563eb',
      visited: '#2563eb',
      hover: '#1d4ed8',
      textDecoration: 'underline',
      hoverTextDecoration: 'underline',
    },
  },
  sidebar: {
    width: '280px',
    backgroundColor: '#ffffff',
    textColor: '#3f3f46',
    activeTextColor: '#2563eb',
    groupItems: {
      activeBackgroundColor: '#f4f4f5',
      activeTextColor: '#2563eb',
      textTransform: 'uppercase',
    },
    level1Items: {
      activeBackgroundColor: '#f4f4f5',
      activeTextColor: '#2563eb',
      textTransform: 'none',
    },
    arrow: {
      size: '1.5em',
      color: '#3f3f46',
    },
  },
  logo: {
    maxHeight: '280px',
    maxWidth: '280px',
    gutter: '2px',
  },
  rightPanel: {
    backgroundColor: '#1e293b',
    width: '42%',
    textColor: '#f8fafc',
    servers: {
      overlay: {
        backgroundColor: '#fafafa',
        textColor: '#1e293b',
      },
      url: {
        backgroundColor: '#ffffff',
      },
    },
  },
  codeBlock: {
    backgroundColor: '#0f172a',
  },
  fab: {
    backgroundColor: '#ffffff',
    color: '#2563eb',
  },
};

@Component({
  selector: 'app-documentation',
  templateUrl: './documentation.component.html',
  styleUrls: ['./documentation.component.css'],
  standalone: false,
})
export class DocumentationComponent implements AfterViewInit {
  @ViewChild('redocContainer', {read: ElementRef})
  private redocContainer?: ElementRef<HTMLElement>;

  ngAfterViewInit(): void {
    this.initDocs();
  }

  initDocs() {
    const container = this.redocContainer?.nativeElement;
    if (!container) {
      return;
    }

    const apiUrl = environment.API_URL;

    document.body.setAttribute('data-theme-version', 'light');
    document.documentElement.setAttribute('data-theme-version', 'light');

    Redoc.init(
      apiUrl + '/docs',
      {
        scrollYOffset: 60,
        hideDownloadButton: true,
        theme: redocTheme,
        nativeScrollbars: true,
      },
      container,
    );
  }
}
